/**
 * The `live` scene: one host publishes, the audience subscribes and interacts.
 *
 * - The audience joins with `publishAudio/publishVideo = false`; the control plane token for the
 *   audience role has `canPublish: false`, so even a manual publish attempt is rejected by the SFU.
 * - Comments and reactions travel over the `live` data topic (real data channel in online mode,
 *   in-process in offline demo mode).
 * - Host management (mute / remove / role) is cooperative: the host broadcasts an intent and the
 *   target applies it, which is the honest limit without a server-side moderation API.
 */
import { useCallback, useEffect, useReducer, useState } from 'react';
import type { MeetingRole } from '../meeting/session';
import { useI18n } from '../i18n';
import { Banner, Modal, RemoteAudio, VideoTile, messageText, useSessionState } from '../ui';
import {
  initialLiveRoom,
  liveReducer,
  reduceLive,
  type LiveReactionKind,
} from '../scenarios/machines';
import { RoomHeader, useNoticeToast } from './common';
import type { RoomContext } from './types';

const REACTIONS: Array<{
  kind: LiveReactionKind;
  key: 'live.reaction.heart' | 'live.reaction.clap' | 'live.reaction.like';
  glyph: string;
}> = [
  { kind: 'heart', key: 'live.reaction.heart', glyph: '❤️' },
  { kind: 'clap', key: 'live.reaction.clap', glyph: '👏' },
  { kind: 'like', key: 'live.reaction.like', glyph: '👍' },
];

export function LiveRoom({ context }: { context: RoomContext }) {
  const { session, client, notify, onLeave } = context;
  const { t } = useI18n();
  const state = useSessionState(
    useCallback((listener) => session.subscribe(listener), [session]),
    useCallback(() => session.getState(), [session]),
  );
  useNoticeToast(session, notify);
  const [live, dispatch] = useReducer(liveReducer, undefined, () =>
    initialLiveRoom(context.role === 'audience' ? 'audience' : 'host'),
  );
  const [draft, setDraft] = useState('');
  const [manageOpen, setManageOpen] = useState(false);

  const local = state.participants.find((entry) => entry.isLocal) ?? null;
  const isHost = (local?.role ?? (context.role as MeetingRole)) === 'host';
  const host = state.participants.find((entry) => entry.role === 'host' && !entry.isLocal) ?? null;
  // The host may be the local participant (hosting) or a remote one; fall back to the first remote
  // so the audience renders the stage even before the presence message lands.
  const stage = isHost
    ? local
    : (host ?? state.participants.find((entry) => !entry.isLocal) ?? null);
  const audience = state.participants.filter((entry) => !entry.isLocal && entry.role !== 'host');
  const stageStream = stage
    ? stage.isLocal
      ? client.getLocalStream('camera')
      : client.getRemoteStream(stage.uid, 'video')
    : null;

  useEffect(() => {
    if (state.phase === 'joined' && state.localUid !== null) {
      dispatch({
        type: 'join',
        localRole: isHost ? 'host' : 'audience',
        localUid: state.localUid,
        hostUid: host?.uid ?? (isHost ? state.localUid : null),
      });
    }
  }, [state.phase, state.localUid, isHost, host?.uid]);

  useEffect(() => {
    for (const participant of state.participants) {
      dispatch({
        type: 'participant',
        uid: participant.uid,
        name: participant.displayName,
        role: participant.role === 'host' ? 'host' : 'audience',
      });
    }
  }, [state.participants]);

  const sendComment = (): void => {
    const text = draft.trim();
    if (!text) return;
    void session.sendLiveComment(text).catch((error) => notify(String(error)));
    setDraft('');
  };

  const moderate = (
    targetUid: number,
    action: 'mute' | 'kick' | 'set-role',
    name: string,
  ): void => {
    const targetRole =
      state.participants.find((entry) => entry.uid === targetUid)?.role ?? 'audience';
    const nextRole: MeetingRole = targetRole === 'host' ? 'audience' : 'host';
    const result = reduceLive(live, {
      type: 'moderate',
      action,
      targetUid,
      ...(action === 'set-role' ? { role: nextRole as 'host' | 'audience' } : {}),
    });
    if (!result.allowed) {
      notify(t(result.reason ?? 'toast.notHost'));
      return;
    }
    if (action === 'mute') {
      session.setRole(targetUid, targetRole);
      notify(t('live.muteToast', { name }));
    } else if (action === 'kick') {
      session.kick(targetUid);
      notify(t('live.kickToast', { name }));
    } else {
      session.setRole(targetUid, nextRole);
      notify(
        t('live.roleToast', { name, role: t(nextRole === 'host' ? 'live.host' : 'live.audience') }),
      );
    }
    void session.hostAction(
      targetUid,
      action === 'set-role' ? 'role' : action,
      action === 'set-role' ? nextRole : undefined,
    );
  };

  const errorText = state.errorKey ? t(state.errorKey) : state.error;

  return (
    <main className="nm-live" data-testid="scene-live" data-scene="live">
      <RoomHeader
        scene="live"
        channel={state.channel ?? ''}
        onLeave={onLeave}
        meta={
          <>
            <span
              className={`nm-badge ${isHost ? 'is-host' : 'is-live'}`}
              data-testid={isHost ? 'live-host-badge' : 'live-audience-badge'}
            >
              {isHost ? t('live.hostBadge') : t('live.audienceBadge')}
            </span>
            <span className="nm-badge" data-testid="live-audience-count">
              {t('live.audienceCount', { count: audience.length })}
            </span>
          </>
        }
        actions={
          <>
            <button
              className="nm-btn"
              onClick={() => {
                void session.renew().then(() => notify(t('room.renewed')));
              }}
              data-testid="live-renew"
            >
              {t('live.renew')}
            </button>
            {isHost ? (
              <button
                className="nm-btn"
                onClick={() => setManageOpen(true)}
                data-testid="live-manage"
              >
                {t('live.manage')}
              </button>
            ) : null}
          </>
        }
      />

      {errorText ? (
        <Banner tone="error">
          <span data-testid="room-error">{errorText}</span>
        </Banner>
      ) : null}
      {!isHost ? (
        <Banner tone="warn">
          <span data-testid="live-audience-note">{t('live.audienceNote')}</span>
        </Banner>
      ) : null}

      <div className="nm-live-grid">
        <section className="nm-live-stage" data-testid="live-stage" aria-label={t('live.stage')}>
          {stage ? (
            <>
              <VideoTile
                participant={stage}
                stream={stageStream}
                spotlight
                className="nm-tile--stage"
              />
              {!stage.isLocal ? (
                <RemoteAudio stream={client.getRemoteStream(stage.uid, 'audio')} />
              ) : null}
            </>
          ) : (
            <div className="nm-live-placeholder">{t('live.stage')}</div>
          )}
          <div className="nm-live-controls" role="toolbar" aria-label={t('room.controls')}>
            <button
              className="nm-btn"
              aria-pressed={state.micEnabled}
              disabled={!isHost}
              title={isHost ? t('meeting.control.micOn') : t('live.publishDenied')}
              data-testid="live-mic"
              onClick={() => {
                if (!isHost) {
                  notify(t('live.publishDeniedToast'));
                  return;
                }
                void session.toggleMic().catch(() => notify(t('live.publishDeniedToast')));
              }}
            >
              {state.micEnabled ? '🎙' : '🔇'}
            </button>
            <button
              className="nm-btn"
              aria-pressed={state.cameraEnabled}
              disabled={!isHost}
              title={isHost ? t('meeting.control.cameraOn') : t('live.publishDenied')}
              data-testid="live-camera"
              onClick={() => {
                if (!isHost) {
                  notify(t('live.publishDeniedToast'));
                  return;
                }
                void session.toggleCamera().catch(() => notify(t('live.publishDeniedToast')));
              }}
            >
              {state.cameraEnabled ? '🎥' : '🚫'}
            </button>
          </div>
        </section>

        <aside className="nm-live-side">
          <div className="nm-card nm-live-comments" data-testid="live-comments">
            <h2>{t('live.comments')}</h2>
            <div className="nm-messages" role="log" aria-live="polite">
              {state.liveComments.length === 0 ? (
                <p className="nm-sub" data-testid="live-comments-empty">
                  {t('live.commentsEmpty')}
                </p>
              ) : null}
              {state.liveComments.map((comment) => (
                <div
                  key={comment.id}
                  className={`nm-msg${comment.kind === 'reaction' ? ' is-reaction' : ''}`}
                  data-testid={`live-comment-${comment.id}`}
                >
                  <div className="nm-msg-head">
                    <span>{comment.fromName}</span>
                    <span>{new Date(comment.at).toLocaleTimeString()}</span>
                  </div>
                  <div className="nm-msg-text">
                    {comment.kind === 'reaction'
                      ? `${t('live.reaction.received')} ${
                          comment.text === 'heart' ? '❤️' : comment.text === 'clap' ? '👏' : '👍'
                        }`
                      : messageText(comment, t)}
                  </div>
                </div>
              ))}
            </div>
            <div className="nm-composer">
              <input
                className="nm-input"
                value={draft}
                placeholder={t('live.commentPlaceholder')}
                aria-label={t('live.commentLabel')}
                data-testid="live-comment-input"
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    sendComment();
                  }
                }}
              />
              <button
                className="nm-btn is-active"
                onClick={sendComment}
                disabled={draft.trim().length === 0}
                data-testid="live-comment-send"
              >
                {t('live.sendComment')}
              </button>
            </div>
          </div>

          <div className="nm-card nm-live-reactions" data-testid="live-reactions">
            <h2>{t('live.reactions')}</h2>
            <div className="nm-row">
              {REACTIONS.map((reaction) => (
                <button
                  key={reaction.kind}
                  className="nm-btn nm-reaction"
                  title={t(reaction.key)}
                  aria-label={t(reaction.key)}
                  data-testid={`live-reaction-${reaction.kind}`}
                  onClick={() => {
                    void session
                      .sendReaction(reaction.kind)
                      .catch((error) => notify(String(error)));
                  }}
                >
                  <span aria-hidden="true">{reaction.glyph}</span>
                  <span
                    className="nm-reaction-count"
                    data-testid={`live-reaction-count-${reaction.kind}`}
                  >
                    {state.reactions[reaction.kind] ?? 0}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {manageOpen ? (
        <Modal
          title={t('live.manageTitle')}
          onClose={() => setManageOpen(false)}
          testId="live-manage-dialog"
        >
          <div className="nm-scroll" style={{ maxHeight: 300 }}>
            {state.participants
              .filter((entry) => !entry.isLocal)
              .map((participant) => (
                <div
                  className="nm-person"
                  key={participant.uid}
                  data-testid={`live-person-${participant.uid}`}
                >
                  <div className="nm-person-main">
                    <div className="nm-person-name">{participant.displayName}</div>
                    <div className="nm-person-meta">
                      {participant.role === 'host' ? t('live.host') : t('live.audience')}
                    </div>
                  </div>
                  <button
                    className="nm-btn"
                    onClick={() => moderate(participant.uid, 'mute', participant.displayName)}
                    data-testid={`live-mute-${participant.uid}`}
                  >
                    {t('live.mute')}
                  </button>
                  <button
                    className="nm-btn"
                    onClick={() => moderate(participant.uid, 'set-role', participant.displayName)}
                    data-testid={`live-role-${participant.uid}`}
                  >
                    {participant.role === 'host' ? t('live.demote') : t('live.promote')}
                  </button>
                  <button
                    className="nm-btn is-danger"
                    onClick={() => moderate(participant.uid, 'kick', participant.displayName)}
                    data-testid={`live-kick-${participant.uid}`}
                  >
                    {t('live.kick')}
                  </button>
                </div>
              ))}
          </div>
        </Modal>
      ) : null}
    </main>
  );
}
